import { useEffect, useRef, useState } from 'react';

function normalizeTitle(title?: string | null): string {
  // Collapse ASCII whitespace runs to a single U+0020 space
  return title ? title.replace(/[\t\n\f\r ]+/g, ' ').trim() : '';
}

export const useDocumentTitle = () => {
  const [title, setTitle] = useState<string>('');
  const observer = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateTitle = () => {
      const titleElement = document.querySelector('title');
      const titleTextContent = normalizeTitle(titleElement?.textContent);
      const documentTitle = normalizeTitle(document?.title);

      if (titleTextContent && titleTextContent !== title) {
        setTitle(titleTextContent);
      } else if (documentTitle && documentTitle !== title) {
        setTitle(documentTitle);
      }
    };

    observer.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          updateTitle();
        }
      });
    });

    updateTitle();

    const titleElement = document.querySelector('title');
    if (titleElement) {
      observer.current.observe(titleElement, {
        subtree: true,
        characterData: true,
        childList: true,
      });
    }

    timeoutRef.current = setTimeout(() => {
      updateTitle();
    }, 1000);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MutationObserver setup runs once on mount
  }, []);

  return title;
};
